import { TextField } from '@boltenergy-be/design-system'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'next-i18next/pages'
import { BELGIAN_ENTERPRISE_NUMBER } from 'constants/regex'
import { checkControlDigit } from './utils'
import boltBackend from 'lib/bolt-backend'
import { useState } from 'react'
import { log } from 'utils/logging'
import { Flow } from 'types/logging'
import { removeNonNumbers } from 'utils/helpers'
import styles from './CompanyNumber.module.scss'
import { InputProps } from 'components/inputs/types'
import { getFieldError, getFieldName } from 'components/inputs/utils'

const CompanyNumber = ({
  checkCredit,
  prefixed,
  identifier = 'companyNumber',
  parentDotPath,
  required = true
}: {
  checkCredit?: boolean
  prefixed?: boolean
} & InputProps) => {
  // React Hook Form
  const {
    formState: { errors },
    register,
    setValue
  } = useFormContext()

  // i18n
  const { t } = useTranslation(['commonFormFields', 'validation', 'common'])

  // Local state
  const [loading, setLoading] = useState<boolean>(false)

  // Constants
  const fieldKey = getFieldName(identifier, parentDotPath)
  const fieldError = getFieldError(identifier, parentDotPath, errors)

  /**
   * Execute the credit check if necessary and return the state or error msg
   * @param {string} enterpriseNumber
   * @returns {Promise<boolean | string | undefined>}
   */
  const executeCreditCheck = async (enterpriseNumber: string): Promise<boolean | string | undefined> => {
    if (!checkCredit) return true

    setLoading(true)

    try {
      const { success, data } = await boltBackend.companyCreditCheck({ enterpriseNumber })

      if (success) {
        return data?.accepted || t(($) => $.companyNumber.errors.creditCheck)
      }

      return true // If the request fails, we don't block the user
    } catch (err) {
      log({
        error: 'Error during credit check',
        identifier: `[${Flow.REGISTRATION}:CompanyNumber]`,
        metaData: { creditCheck: { enterpriseNumber } }
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <TextField
      {...register(fieldKey, {
        pattern: { value: BELGIAN_ENTERPRISE_NUMBER, message: t(($) => $.companyNumber.errors.invalid) },
        required: required ? t(($) => $.required, { ns: 'validation' }) : false,
        validate: {
          validCompanyNumber: (companyNumber: string) => checkControlDigit(companyNumber) || t(($) => $.companyNumber.errors.invalid),
          creditCheck: async (companyNumber: string) => await executeCreditCheck(companyNumber)
        }
      })}
      onBlur={(event) => setValue(fieldKey, removeNonNumbers(event.target.value), { shouldValidate: true })}
      error={fieldError?.message}
      label={t(($) => $.companyNumber.label)}
      disabled={loading}
      prefix={prefixed ? { as: 'BE', className: styles.prefix } : undefined}
      placeholder={prefixed ? '0123 456 789' : undefined}
      {...(!required && { optionalText: t(($) => $.optional, { ns: 'common' }) })}
    />
  )
}

export default CompanyNumber
